import React, { useState } from 'react';
import { Brain, Home, Hand, ListOrdered, Printer, Lightbulb, CalendarCheck, ClipboardList, BookOpen, LayoutGrid, ListChecks, Gift, Check, ShieldCheck, Star, ChevronDown, Layers, FileText, RefreshCw, Puzzle, FolderOpen, Clock, Mail } from 'lucide-react';
import { BuyButton } from '../components/BuyButton';
import { Mockup } from '../components/Mockup';
import { MetaPixel, trackCheckout } from '../components/MetaPixel';
import { PurchasePopup } from '../components/PurchasePopup';
export const args = {
  price: '$19.990 ARS',
  checkoutUrl: 'https://terapia-lista.impultienda.ar/checkout',
  showPopup: true,
  openFaq: 0
};
export const argTypes = {
  price: {
    control: 'text'
  },
  checkoutUrl: {
    control: 'text'
  },
  showPopup: {
    control: 'boolean'
  },
  openFaq: {
    control: 'number'
  }
};
type Props = Partial<typeof args>;
export default function PaginaDeVentas(props: Props) {
  const {
    price,
    checkoutUrl,
    showPopup,
    openFaq
  } = {
    ...args,
    ...props
  };
  const [open, setOpen] = useState<number>(openFaq);
  return <main className="w-full overflow-x-hidden bg-white font-sans text-ink" data-id="element-19" data-mp="2r39h4565rwvt">
      <MetaPixel data-id="element-20" data-mp="2r15errnwr5xn" />
      <PurchasePopup enabled={showPopup} data-id="element-21" data-mp="2m3n33tiiacly" />

      {/* ───────── HERO ───────── */}
      <section className="bg-white" data-id="element-22" data-mp="2m5q4tat0tu6i">
        <div className="bg-brand py-2 text-center" data-id="element-23" data-mp="2h5ll7bwtgjc2">
          <span className="font-display text-lg tracking-wide text-white sm:text-xl" data-id="element-24" data-mp="2h6wnt8eizl1l">TERAPIA LISTA</span>
        </div>
        <div className="mx-auto max-w-5xl px-4 pb-8 pt-5 text-center md:pb-10 md:pt-7" data-id="element-25" data-mp="28cic1ft7esn8">
          <h1 className="font-display text-[30px] uppercase leading-[1.05] text-brand sm:text-4xl md:text-5xl" data-id="element-26" data-mp="28annzsmmykex">
            EL SISTEMA PROFESIONAL DE ACTIVIDADES Y RECURSOS VISUALES PARA{' '}
            <span className="bg-gold px-1.5 text-brand-dark" data-id="element-27" data-mp="1ycho1gke3ocp">TERAPIA OCUPACIONAL</span>
          </h1>
          <p className="mx-auto mt-3 max-w-3xl rounded-lg bg-brand px-3 py-2.5 text-[13px] font-extrabold uppercase leading-snug text-white sm:text-base" data-id="element-28" data-mp="1th2daxhoi8ki">
            DEJÁ DE BUSCAR ENTRE MATERIALES DESORDENADOS. EMPEZÁ A ORGANIZAR LAS ACTIVIDADES QUE UTILIZÁS EN TUS SESIONES.
          </p>
          <div className="mt-4 grid items-center gap-4 md:grid-cols-[1.05fr_1fr] md:gap-8 md:text-left" data-id="element-29" data-mp="1jpj03vkqrc2q">
            <Mockup className="mx-auto max-w-[360px] md:max-w-none" data-id="element-30" data-mp="15u74llyrhhkr" />
            <div className="flex flex-col gap-4" data-id="element-31" data-mp="1arh27owb0zjo">
              <p className="text-[15px] leading-relaxed text-ink/85 md:text-base" data-id="element-32" data-mp="1ygrx4pqz3zpw">
                Accedé a actividades de estimulación cognitiva, propuestas funcionales, fichas imprimibles, organizadores visuales y herramientas de planificación, todo reunido en un sistema digital diseñado para acompañar el trabajo en Terapia Ocupacional con adultos y personas mayores.
              </p>
              <BuyButton data-id="element-33" data-mp="2hfdym5qejsuc">QUIERO MI KIT TERAPIA LISTA AHORA</BuyButton>
              <p className="flex items-center justify-center gap-1.5 text-[13px] font-semibold text-brand md:justify-start" data-id="element-34" data-mp="2he2uu5wyil38">
                <ShieldCheck className="h-4 w-4 shrink-0 text-gold-dark" data-id="element-35" data-mp="2vkwcjcm2bfyj" />
                Garantía de 7 días según las condiciones de devolución.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── PROBLEMA ───────── */}
      <section className="bg-cream py-8 md:py-12" data-id="element-36" data-mp="359xs51op3vbz">
        <div className="mx-auto max-w-5xl px-4" data-id="element-37" data-mp="30b4ftcl5aade">
          <h2 className="text-center font-display text-3xl uppercase text-brand md:text-4xl" data-id="element-38" data-mp="30cecpyg9hcig">¿TE SUENA FAMILIAR?</h2>
          <p className="mx-auto mt-3 max-w-3xl text-center text-[15px] leading-relaxed text-ink/85" data-id="element-39" data-mp="2cnv5zncv17sh">
            Estás preparando actividades, trabajando la estimulación cognitiva, buscando propuestas para la vida diaria y organizando materiales para tus sesiones, pero tus recursos están repartidos entre diferentes archivos, páginas, documentos y manuales.
          </p>
          <div className="mt-5 grid gap-3 md:grid-cols-3" data-id="element-40" data-mp="1sfo75c1n8gu">
            {PROBLEMS.map(p => <div key={p.title} className="flex flex-col rounded-xl border-l-4 border-mod-red bg-white p-4 shadow-sm" data-id="element-41" data-mp="3szs29bhmr5sj">
                <p.icon className="h-6 w-6 text-mod-red" data-id="element-42" data-mp="22lanb9ogrwsj" />
                <h3 className="mt-2 text-[15px] font-black uppercase leading-tight text-brand" data-id="element-43" data-mp="27jcb27wu4kac">{p.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-ink/80" data-id="element-44" data-mp="2cgkoxwaya25s">{p.body}</p>
              </div>)}
          </div>
          <div className="mt-4 rounded-xl bg-brand p-4 text-center text-white md:p-5" data-id="element-45" data-mp="2ggml5pxx23do">
            <h3 className="font-display text-2xl uppercase leading-tight text-gold md:text-3xl" data-id="element-46" data-mp="2lfy517tkxozh">DEJÁ DE ORGANIZAR TUS RECURSOS DESDE CERO.</h3>
            <p className="mx-auto mt-2 max-w-3xl text-sm leading-relaxed text-white/90 md:text-[15px]" data-id="element-47" data-mp="2v7c093ei02mt">
              Dejá atrás la búsqueda constante de materiales aislados y reuní tus principales recursos de trabajo en un sistema organizado que puedas consultar, imprimir y adaptar según tus objetivos profesionales.
            </p>
          </div>
        </div>
      </section>

      {/* ───────── SISTEMA / MÓDULOS ───────── */}
      <section className="bg-white py-8 md:py-12" data-id="element-48" data-mp="xl2tik8alqyf">
        <div className="mx-auto max-w-5xl px-4" data-id="element-49" data-mp="slgdk312xwlx">
          <div className="text-center" data-id="element-50" data-mp="sjn5cjatj4bt">
            <h2 className="font-display text-3xl uppercase leading-tight text-brand md:text-[42px]" data-id="element-51" data-mp="ojle742pltas">
              TODO LO QUE NECESITÁS PARA ORGANIZAR TUS RECURSOS. UN SOLO SISTEMA.
            </h2>
            <p className="mt-2 inline-block rounded-full bg-gold px-3 py-1 text-xs font-extrabold uppercase text-brand-dark sm:text-sm" data-id="element-52" data-mp="1garfhnu61x1n">
              6 MÓDULOS PROFESIONALES EN UN SOLO KIT
            </p>
            <h3 className="mt-4 font-display text-2xl uppercase text-brand" data-id="element-53" data-mp="17i7eigqejgdn">TERAPIA LISTA</h3>
            <p className="text-sm font-extrabold uppercase text-brand/80" data-id="element-54" data-mp="12je2ytuodzg4">KIT COMPLETO DE ACTIVIDADES PARA TERAPIA OCUPACIONAL</p>
            <p className="mx-auto mt-2 max-w-3xl text-[15px] leading-relaxed text-ink/85" data-id="element-55" data-mp="1zz0ccb6nmr7h">
              Terapia Lista reúne actividades, fichas, propuestas funcionales y herramientas de planificación en seis módulos organizados para facilitar la preparación de sesiones con adultos y personas mayores.
            </p>
            <p className="mx-auto mt-3 max-w-3xl rounded-lg border-2 border-sage bg-cream px-2 py-2 text-[11px] font-black uppercase tracking-tight text-brand sm:text-sm" data-id="element-56" data-mp="2j225olar0x57">
              SELECCIONÁ → ORGANIZÁ → ADAPTÁ → PLANIFICÁ → APLICÁ → REGISTRÁ
            </p>
          </div>

          <div className="mt-5 grid gap-3 md:grid-cols-2" data-id="element-57" data-mp="1vcr9zi899sxx">
            {MODULES.map((m, i) => <article key={m.title} className="flex gap-3 overflow-hidden rounded-xl border border-black/5 bg-cream p-3.5" style={{
            borderLeft: `6px solid ${m.color}`
          }} data-id="element-58" data-mp="20dx3hi9qpk8i" data-mpi={i}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-white" style={{
              background: m.color
            }} data-id="element-59" data-mp="1qefyl32bbadl">
                  <m.icon className="h-6 w-6" data-id="element-60" data-mp="3gsgh1xr7dc3z" />
                </span>
                <div className="min-w-0" data-id="element-61" data-mp="3kt8qos3zlj0s">
                  <h3 className="text-[13.5px] font-black uppercase leading-tight" style={{
                color: m.color
              }} data-id="element-62" data-mp="3krxqf29r2tef">
                    MÓDULO {i + 1} — {m.title}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-ink/80" data-id="element-63" data-mp="155380x9sa85y">{m.body}</p>
                </div>
              </article>)}
          </div>
        </div>
      </section>

      {/* ───────── MIRÁ LO QUE INCLUYE ───────── */}
      <section className="bg-brand py-8 text-white md:py-12" data-id="element-64" data-mp="1e2nfwp25j2pj">
        <div className="mx-auto grid max-w-5xl items-center gap-5 px-4 md:grid-cols-[1fr_1.1fr] md:gap-8" data-id="element-65" data-mp="1srt8vvaebwoz">
          <div className="text-center md:text-left" data-id="element-66" data-mp="1nu04n7snlcrc">
            <h2 className="font-display text-3xl uppercase leading-tight text-gold md:text-4xl" data-id="element-67" data-mp="21uknu52hdald">MIRÁ LO QUE INCLUYE TERAPIA LISTA</h2>
            <p className="mt-1 text-sm font-extrabold uppercase text-sage" data-id="element-68" data-mp="1wvhiejil92cv">CONOCÉ LOS RECURSOS QUE VAS A TENER A TU DISPOSICIÓN</p>
            <p className="mt-3 text-[15px] leading-relaxed text-white/90" data-id="element-69" data-mp="7hutxnc441dz">
              Explorá una colección organizada de actividades cognitivas, propuestas funcionales, fichas imprimibles y organizadores de rutinas, complementada con recursos para planificar sesiones y adaptar actividades.
            </p>
            <p className="mt-2 text-[15px] leading-relaxed text-white/90" data-id="element-70" data-mp="3miyorw69c4p5">
              La estructura está pensada para ayudarte a encontrar propuestas, preparar tus materiales y organizar mejor tu trabajo profesional.
            </p>
          </div>
          <a href="#compra" className="block overflow-hidden rounded-xl border-4 border-gold" aria-label="Ir a la sección de compra" data-id="element-71" data-mp="2ut3y9w6ny8yw">
            <img src="https://cdn.magicpatterns.com/patterns/generated-images/f763f51e-9074-49c4-be92-e73bc069b289.jpg" alt="Actividad de motricidad fina con cuentas de colores" className="aspect-[4/3] w-full object-cover transition-transform duration-200 ease-out hover:scale-[1.03]" data-id="element-72" data-mp="2yv7e0x0d7zpz" />
          </a>
        </div>
      </section>

      {/* ───────── BONOS ───────── */}
      <section className="bg-cream py-8 md:py-12" data-id="element-73" data-mp="28j3z10g4ll9d">
        <div className="mx-auto max-w-5xl px-4" data-id="element-74" data-mp="28ilu7ffedypu">
          <h2 className="text-center font-display text-3xl uppercase text-brand md:text-4xl" data-id="element-75" data-mp="23jr1nlrmovsj">
            <span className="text-gold-dark" data-id="element-76" data-mp="1ytape4zjng7r">5</span> BONOS COMPLEMENTARIOS INCLUIDOS
          </h2>
          <div className="mt-5 grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-id="element-77" data-mp="1tuqxzp26lc80">
            {BONUSES.map((b, i) => <article key={b.title} className={`flex gap-3 rounded-xl border-2 border-gold bg-white p-3.5 ${i === 4 ? 'md:col-span-2 lg:col-span-1' : ''}`} data-id="element-78" data-mp="1pt5rqmk5jw6s" data-mpi={i}>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gold text-brand-dark" data-id="element-79" data-mp="1kw5muez1gzh9">
                  <b.icon className="h-5 w-5" data-id="element-80" data-mp="1avv82pxcvjzz" />
                </span>
                <div className="min-w-0" data-id="element-81" data-mp="1axpwie7u5u8t">
                  <p className="flex items-center gap-1 text-[11px] font-black uppercase text-gold-dark" data-id="element-82" data-mp="3a8zbyv8vyd81">
                    <Gift className="h-3.5 w-3.5" data-id="element-83" data-mp="359e8q8monipi" /> BONO {i + 1}
                  </p>
                  <h3 className="text-[13.5px] font-black uppercase leading-tight text-brand" data-id="element-84" data-mp="m6ra8pf0ah2z">{b.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-ink/80" data-id="element-85" data-mp="h8z7f0ctpak0">{b.body}</p>
                </div>
              </article>)}
          </div>
        </div>
      </section>

      {/* ───────── NO ES OTRO EBOOK ───────── */}
      <section className="bg-white py-8 md:py-12" data-id="element-86" data-mp="r7585emw8ana">
        <div className="mx-auto max-w-5xl px-4" data-id="element-87" data-mp="3g9jgqdqhvk9">
          <div className="text-center" data-id="element-88" data-mp="3h2o9lt4uazr">
            <h2 className="font-display text-3xl uppercase leading-tight text-brand md:text-4xl" data-id="element-89" data-mp="3unm5pjth4z71">ESTO NO ES SOLAMENTE OTRO EBOOK</h2>
            <p className="mt-1 text-sm font-extrabold uppercase text-gold-dark md:text-base" data-id="element-90" data-mp="d7dzzuj25oh7">ES UN SISTEMA ORGANIZADO DE RECURSOS PARA TERAPIA OCUPACIONAL</p>
          </div>
          <ul className="mt-5 grid gap-x-8 gap-y-3 md:grid-cols-2" data-id="element-91" data-mp="3gv60ccd3op2y">
            {PILLARS.map(p => <li key={p.title} className="flex gap-3 border-b border-sage/60 pb-3" data-id="element-92" data-mp="3bxe4x8d8yku0">
                <p.icon className="mt-0.5 h-6 w-6 shrink-0 text-brand" data-id="element-93" data-mp="1re20yjkxotxe" />
                <div data-id="element-94" data-mp="1rcqw0gfr5g4p">
                  <h3 className="text-sm font-black uppercase text-brand" data-id="element-95" data-mp="1w5sf70mzs1u3">{p.title}</h3>
                  <p className="mt-0.5 text-sm leading-relaxed text-ink/80" data-id="element-96" data-mp="1hge1xhgap09f">{p.body}</p>
                </div>
              </li>)}
          </ul>
          <p className="mt-5 text-center text-base font-extrabold text-brand md:text-lg" data-id="element-97" data-mp="1mg0fxt3y6kja">
            Menos búsquedas desordenadas. Más recursos organizados. Más alternativas para planificar tus sesiones.
          </p>
        </div>
      </section>

      {/* ───────── OFERTA / COMPRA ───────── */}
      <section id="compra" className="scroll-mt-2 bg-brand py-8 md:py-12" data-id="element-98" data-mp="213tvxewy3p2e">
        <div className="mx-auto max-w-3xl px-3" data-id="element-99" data-mp="254xck3mthp4o">
          <div className="overflow-hidden rounded-2xl border-4 border-dashed border-gold bg-white" data-id="element-100" data-mp="257tahryo0dt4">
            <div className="bg-gold py-2 text-center text-sm font-black uppercase tracking-wide text-brand-dark sm:text-base" data-id="element-101" data-mp="prkpzvwxe95n">
              OFERTA ESPECIAL DE LANZAMIENTO
            </div>
            <div className="p-4 text-center sm:p-6" data-id="element-102" data-mp="2wfjnrjt3phra">
              <h2 className="font-display text-4xl uppercase text-brand md:text-5xl" data-id="element-103" data-mp="2rff79ts70n12">TERAPIA LISTA</h2>
              <p className="text-sm font-extrabold uppercase text-brand/80" data-id="element-104" data-mp="2mmm43acdhmr3">KIT COMPLETO DE ACTIVIDADES PARA TERAPIA OCUPACIONAL</p>
              <p className="mt-1 text-sm text-ink/80" data-id="element-105" data-mp="2hoaun0pzpe9e">Un sistema digital con seis módulos y cinco bonos complementarios.</p>
              <Mockup href={checkoutUrl} onClick={trackCheckout} className="mx-auto mt-2 max-w-[340px]" data-id="element-106" data-mp="28nw4no22zazk" />
              <ul className="mx-auto mt-2 max-w-md space-y-1.5 text-left" data-id="element-107" data-mp="23ot7sqw98b2o">
                {OFFER.map(o => <li key={o} className="flex gap-2 text-sm font-semibold leading-snug text-ink" data-id="element-108" data-mp="1yq88ctottvfa">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand text-white" data-id="element-109" data-mp="1xyblxy4y9vf">
                      <Check className="h-3.5 w-3.5" strokeWidth={3} data-id="element-110" data-mp="3t512eoa2ae54" />
                    </span>
                    {o}
                  </li>)}
              </ul>
              <p className="mt-4 text-base font-black uppercase leading-tight text-brand sm:text-lg" data-id="element-111" data-mp="9g5xgn8fvtlv">
                CONSEGUÍ TERAPIA LISTA POR <span className="mt-1 block font-display text-5xl text-mod-red sm:text-6xl" data-id="element-112" data-mp="e87iaxjoasni">{price}</span>
              </p>
              <div className="mt-3" data-id="element-113" data-mp="j8lglnvh2o8c">
                <BuyButton href={checkoutUrl} onClick={trackCheckout} data-id="element-114" data-mp="3ime4xk9fg3ul">QUIERO ACCEDER A TERAPIA LISTA</BuyButton>
              </div>
              <p className="mt-2 text-xs text-ink/60" data-id="element-115" data-mp="3mnzlpgp312a2">Acceso digital según las condiciones de entrega de la tienda.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── GARANTÍA ───────── */}
      <section className="bg-cream py-8 md:py-10" data-id="element-116" data-mp="e6hly95uvki">
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-4 px-4 text-center md:flex-row md:text-left" data-id="element-117" data-mp="16x4s0c9tjh4n">
          <div className="flex h-28 w-28 shrink-0 flex-col items-center justify-center rounded-full border-[5px] border-double border-gold-dark bg-gold text-brand-dark" data-id="element-118" data-mp="1bu4yupehsnwt">
            <span className="font-display text-4xl leading-none" data-id="element-119" data-mp="2fu1sdm6douu0">7</span>
            <span className="text-[11px] font-black uppercase" data-id="element-120" data-mp="2tt1iky9jhhsa">DÍAS</span>
            <ShieldCheck className="mt-0.5 h-4 w-4" data-id="element-121" data-mp="2tqyh9i2xs289" />
          </div>
          <div data-id="element-122" data-mp="2ot6qire12m5n">
            <p className="text-xs font-black uppercase text-gold-dark" data-id="element-123" data-mp="2osewvm3zqyi0">TU COMPRA ESTÁ RESPALDADA</p>
            <h2 className="font-display text-3xl uppercase text-brand" data-id="element-124" data-mp="33pnxl6f6peme">GARANTÍA DE 7 DÍAS</h2>
            <p className="mt-1 text-sm leading-relaxed text-ink/85" data-id="element-125" data-mp="2yrmh8t957tem">
              Queremos que puedas conocer Terapia Lista y revisar si sus recursos se adaptan a tus necesidades.
            </p>
            <p className="mt-1 text-sm leading-relaxed text-ink/85" data-id="element-126" data-mp="38ih6m6hor94a">
              Disponés de un plazo de 7 días desde la compra para solicitar un reembolso conforme a las condiciones de garantía y al procedimiento de devolución publicados en la tienda.
            </p>
          </div>
        </div>
      </section>

      {/* ───────── TESTIMONIOS ───────── */}
      <section className="bg-white py-8 md:py-12" data-id="element-127" data-mp="9ynu832t2tp7">
        <div className="mx-auto max-w-5xl px-4" data-id="element-128" data-mp="9xcqg39d1ly3">
          <h2 className="text-center font-display text-3xl uppercase leading-tight text-brand md:text-4xl" data-id="element-129" data-mp="3r6rephhfvef2">
            ¿QUÉ PODRÍAN DECIR LOS PROFESIONALES SOBRE ESTE SISTEMA?
          </h2>
          <p className="mx-auto mt-2 max-w-3xl rounded-lg border border-gold bg-gold/10 px-3 py-2 text-center text-xs leading-relaxed text-ink/80" data-id="element-130" data-mp="3ideavkhu6se7">
            Los siguientes textos son ejemplos ilustrativos, no testimonios reales. Deben reemplazarse por opiniones auténticas de compradores antes de publicar esta sección.
          </p>
          <div className="mt-5 grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-id="element-131" data-mp="3dfmh0kxmsw7d">
            {TESTIMONIALS.map((t, i) => <figure key={i} className="flex flex-col rounded-xl bg-cream p-4" data-id="element-132" data-mp="2zfv7awe5e1z6" data-mpi={i}>
                <div className="flex items-center justify-between gap-2" data-id="element-133" data-mp="33g5gehx8l8i9">
                  <span className="text-[11px] font-black uppercase text-brand/70" data-id="element-134" data-mp="19y7xe7ynukpm">TESTIMONIO ILUSTRATIVO {i + 1}</span>
                  <span className="flex text-gold" aria-label="★★★★★" data-id="element-135" data-mp="156g43po4e0vs">
                    {[0, 1, 2, 3, 4].map(s => <Star key={s} className="h-3.5 w-3.5 fill-current" data-id="element-136" data-mp="10625ey8es3af" />)}
                  </span>
                </div>
                <blockquote className="mt-2 text-sm font-medium leading-relaxed text-ink" data-id="element-137" data-mp="1sxc06yzlzi8u">{t.quote}</blockquote>
                <figcaption className="mt-auto pt-3 text-xs font-semibold italic text-brand" data-id="element-138" data-mp="1nyqzyzkcwyke">{t.by}</figcaption>
              </figure>)}
          </div>
        </div>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className="bg-cream py-8 md:py-12" data-id="element-139" data-mp="1ewrhe2tp1mfc">
        <div className="mx-auto max-w-3xl px-4" data-id="element-140" data-mp="7jz7kmn7nrb3">
          <h2 className="text-center font-display text-3xl uppercase text-brand md:text-4xl" data-id="element-141" data-mp="7ksilzt7d2z1">PREGUNTAS FRECUENTES</h2>
          <div className="mt-4 divide-y divide-sage overflow-hidden rounded-xl border border-sage bg-white" data-id="element-142" data-mp="3k8iqoh8vl97">
            {FAQ.map((f, i) => {
            const isOpen = open === i;
            return <div key={f.q} data-id="element-143" data-mp="2o5w5nweisn5p" data-mpi={i}>
                  <button type="button" onClick={() => setOpen(isOpen ? -1 : i)} aria-expanded={isOpen} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-extrabold text-brand transition-colors duration-150 ease-out hover:bg-cream" data-id="element-144" data-mp="2o5nss4iedhui">
                    {f.q}
                    <ChevronDown className={`h-4 w-4 shrink-0 transition-transform duration-200 ease-out ${isOpen ? 'rotate-180' : ''}`} data-id="element-145" data-mp="36w5cjjl9533q" />
                  </button>
                  {isOpen && <div className="px-4 pb-3 text-sm leading-relaxed text-ink/80" data-id="element-146" data-mp="1cj5r39u6xeoj">
                      {f.a}
                      {f.email && <a href={`mailto:${f.email}`} className="mt-1 flex items-center gap-1.5 font-bold text-brand underline" data-id="element-147" data-mp="1hjacne9ygzlm">
                          <Mail className="h-4 w-4" data-id="element-148" data-mp="3fdxnzd2hha6p" /> {f.email}
                        </a>}
                    </div>}
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* ───────── ÚLTIMA PREGUNTA + CTA FINAL ───────── */}
      <section className="bg-white py-8 md:py-12" data-id="element-149" data-mp="30fdetfmwgy4v">
        <div className="mx-auto max-w-3xl px-4 text-center" data-id="element-150" data-mp="30i80fahngozv">
          <h2 className="font-display text-3xl uppercase text-brand md:text-4xl" data-id="element-151" data-mp="2wfv4fdspg5ga">UNA ÚLTIMA PREGUNTA</h2>
          <p className="mt-2 text-[15px] leading-relaxed text-ink/85" data-id="element-152" data-mp="3af485dzq573d">
            Cuando estás preparando actividades de estimulación cognitiva, buscando propuestas funcionales, organizando fichas imprimibles o pensando cómo adaptar una tarea a las necesidades de una persona...
          </p>
          <p className="mt-2 text-base font-extrabold leading-snug text-brand md:text-lg" data-id="element-153" data-mp="ko7lx30sio4j">
            ¿Tenés un sistema organizado que reúna tus actividades y herramientas de planificación en un mismo lugar?
          </p>
          <p className="mt-2 text-[15px] leading-relaxed text-ink/85" data-id="element-154" data-mp="27n0ebofhbxlw">
            Dejá de depender exclusivamente de materiales dispersos y búsquedas constantes para encontrar nuevas propuestas.
          </p>

          <div className="mt-5 rounded-2xl border-4 border-brand bg-cream p-4 sm:p-6" data-id="element-155" data-mp="1yn501aqmnmhn">
            <h3 className="font-display text-4xl uppercase text-brand" data-id="element-156" data-mp="2mbeneh3hoiaw">TERAPIA LISTA</h3>
            <p className="text-sm font-extrabold uppercase text-brand/80" data-id="element-157" data-mp="2qboww3qhpqui">
              EL SISTEMA PROFESIONAL DE ACTIVIDADES Y RECURSOS PARA TERAPIA OCUPACIONAL
            </p>
            <p className="mt-1 text-sm font-bold text-gold-dark" data-id="element-158" data-mp="2hiv2ez2h2kw2">Seleccioná. Organizá. Adaptá. Planificá.</p>
            <Mockup className="mx-auto mt-2 max-w-[320px]" data-id="element-159" data-mp="358pe3db223eq" />
            <p className="mt-2 text-base font-black uppercase leading-tight text-brand sm:text-lg" data-id="element-160" data-mp="39zxpgevy3yuj">
              CONSEGUÍ EL KIT COMPLETO POR <span className="text-mod-red" data-id="element-161" data-mp="3qazsyw0drpf3">{price}</span>
            </p>
            <div className="mt-3" data-id="element-162" data-mp="3v9b0v1747puo">
              <BuyButton data-id="element-163" data-mp="3v89rr29ntdfl">QUIERO TERAPIA LISTA</BuyButton>
            </div>
            <p className="mt-2 text-xs text-ink/60" data-id="element-164" data-mp="3hi69snhtc5vy">
              Acceso digital según las condiciones de entrega. Garantía de 7 días sujeta a los términos de devolución publicados.
            </p>
          </div>
        </div>
      </section>

      {/* ───────── FOOTER ───────── */}
      <footer className="bg-brand-dark px-4 pb-20 pt-6 text-center text-xs leading-relaxed text-white/75" data-id="element-165" data-mp="3citdlc51xmgp">
        <div className="mx-auto max-w-3xl space-y-2" data-id="element-166" data-mp="2yjah5gllrzk8">
          <p className="font-bold text-white" data-id="element-167" data-mp="2yklnno6fnlf1">© 2026 Nova Market — Terapia Lista. Todos los derechos reservados.</p>
          <p data-id="element-168" data-mp="wr5scdzehidq">
            Terapia Lista es un recurso educativo independiente. No otorga certificación profesional ni reemplaza la formación, la evaluación individual, el criterio clínico ni los protocolos profesionales aplicables. Las actividades deben ser seleccionadas y adaptadas por profesionales cualificados.
          </p>
          <p data-id="element-169" data-mp="2h9zk2wonokgs">
            Contacto y soporte:{' '}
            <a href="mailto:novaamarket034@gmail.com" className="font-semibold text-sage underline" data-id="element-170" data-mp="2h73npcsgi3ug">
              novaamarket034@gmail.com
            </a>
          </p>
          <p className="font-semibold text-white/90" data-id="element-171" data-mp="2q935w8f7jdyz">Términos y condiciones · Política de privacidad · Contacto</p>
        </div>
      </footer>
    </main>;
}
const PROBLEMS = [{
  icon: Clock,
  title: 'DEMASIADAS ACTIVIDADES. POCO TIEMPO PARA ORGANIZARLAS.',
  body: '¿Necesitás propuestas para trabajar la memoria, la atención, la coordinación o las habilidades funcionales? Encontrar actividades adecuadas, preparar las fichas y organizar cada encuentro puede llevarte más tiempo del necesario.'
}, {
  icon: Puzzle,
  title: 'TENÉS UNA SESIÓN PREPARADA, PERO TE FALTAN RECURSOS.',
  body: 'A veces necesitás una actividad diferente, una nueva consigna o una alternativa para adaptar una propuesta a las necesidades de una persona. Disponer de recursos organizados facilita la búsqueda de opciones.'
}, {
  icon: FolderOpen,
  title: 'TUS MATERIALES ESTÁN REPARTIDOS POR TODAS PARTES.',
  body: 'Fichas imprimibles, ejercicios, organizadores, ideas de actividades y plantillas de planificación. La información existe, pero encontrarla y mantenerla ordenada puede convertirse en una tarea adicional.'
}];
const MODULES = [{
  color: '#124C38',
  icon: Brain,
  title: 'ESTIMULACIÓN COGNITIVA Y FUNCIONES COGNITIVAS',
  body: 'Explorá actividades relacionadas con memoria, atención, concentración, orientación temporal, asociación de información y resolución de problemas cotidianos mediante propuestas organizadas y adaptables.'
}, {
  color: '#C8312B',
  icon: Home,
  title: 'ACTIVIDADES DE LA VIDA DIARIA Y AUTONOMÍA',
  body: 'Encontrá ideas para trabajar rutinas, secuencias de tareas, organización personal, actividades domésticas y situaciones cotidianas vinculadas con la participación y la autonomía.'
}, {
  color: '#7A4A24',
  icon: Hand,
  title: 'MOTRICIDAD FINA Y COORDINACIÓN',
  body: 'Consultá propuestas relacionadas con la manipulación de objetos, la coordinación óculo-manual, la precisión de movimientos y las habilidades motoras implicadas en distintas tareas funcionales.'
}, {
  color: '#1F63C6',
  icon: ListOrdered,
  title: 'ATENCIÓN, ORGANIZACIÓN Y SECUENCIACIÓN',
  body: 'Trabajá con actividades de clasificación, seguimiento de instrucciones, ordenamiento de pasos, organización de información y planificación de tareas cotidianas.'
}, {
  color: '#E57A12',
  icon: Printer,
  title: 'FICHAS Y MATERIALES IMPRIMIBLES',
  body: 'Reuní ejercicios, fichas de trabajo, organizadores visuales y hojas de actividades que podés imprimir y adaptar de acuerdo con los objetivos de cada sesión.'
}, {
  color: '#6C35B8',
  icon: Lightbulb,
  title: 'BANCO DE IDEAS Y ADAPTACIÓN DE ACTIVIDADES',
  body: 'Accedé a ideas para variar propuestas, simplificar consignas, incorporar apoyos visuales y modificar la complejidad de las actividades según las características y necesidades de cada persona.'
}];
const BONUSES = [{
  icon: CalendarCheck,
  title: 'PLANIFICADOR PROFESIONAL DE SESIONES',
  body: 'Organizá tus encuentros con plantillas para definir objetivos, seleccionar actividades, preparar materiales, anticipar adaptaciones y registrar observaciones relevantes.'
}, {
  icon: ClipboardList,
  title: 'REGISTRO DE OBSERVACIÓN Y SEGUIMIENTO',
  body: 'Reuní información sobre las actividades realizadas, los apoyos utilizados, las dificultades observadas y los aspectos que conviene retomar en próximas sesiones.'
}, {
  icon: BookOpen,
  title: 'GUÍA PRÁCTICA PARA ADAPTAR ACTIVIDADES',
  body: 'Consultá criterios y ejemplos para simplificar instrucciones, dividir tareas en pasos, incorporar apoyos visuales y graduar la dificultad de las propuestas.'
}, {
  icon: LayoutGrid,
  title: 'ORGANIZADORES VISUALES DE RUTINAS COTIDIANAS',
  body: 'Utilizá plantillas para estructurar rutinas diarias, organizar tareas y presentar secuencias de actividades de manera visual y personalizable.'
}, {
  icon: ListChecks,
  title: 'CHECKLIST DE PREPARACIÓN DE SESIONES',
  body: 'Revisá tus objetivos, comprobá los materiales necesarios, organizá el espacio y verificá los aspectos importantes antes y después de cada encuentro.'
}];
const PILLARS = [{
  icon: Puzzle,
  title: 'ACTIVIDADES Y PROPUESTAS FUNCIONALES',
  body: 'Accedé a ideas de trabajo relacionadas con estimulación cognitiva, habilidades motoras y actividades cotidianas.'
}, {
  icon: FileText,
  title: 'FICHAS Y RECURSOS IMPRIMIBLES',
  body: 'Consultá materiales organizados para preparar actividades y adaptarlos según los objetivos profesionales.'
}, {
  icon: Layers,
  title: '6 MÓDULOS ESPECIALIZADOS',
  body: 'Reuní actividades cognitivas, recursos funcionales, coordinación, secuenciación, fichas imprimibles y propuestas para organizar nuevas actividades.'
}, {
  icon: Gift,
  title: '5 BONOS PRÁCTICOS',
  body: 'Complementá el contenido principal con herramientas de planificación, observación, adaptación de actividades y organización de rutinas.'
}, {
  icon: RefreshCw,
  title: 'RECURSOS PARA CONSULTAR UNA Y OTRA VEZ',
  body: 'Conservá tus materiales digitales como biblioteca de consulta para seleccionar propuestas y preparar tus sesiones cuando lo necesites.'
}];
const OFFER = ['6 módulos de actividades y recursos.', 'Propuestas de estimulación cognitiva y actividades funcionales.', 'Actividades relacionadas con la vida diaria y la motricidad fina.', 'Fichas y materiales imprimibles.', 'Herramientas para organizar y adaptar actividades.', '5 bonos complementarios para planificación y seguimiento.'];
const TESTIMONIALS = [{
  quote: '“Me resulta práctico tener distintas categorías de actividades organizadas en un mismo lugar. Es un buen punto de partida para preparar mis sesiones.”',
  by: '— Ejemplo ilustrativo de una profesional de Terapia Ocupacional.'
}, {
  quote: '“Las propuestas de rutinas y actividades funcionales me ayudan a pensar alternativas según los objetivos que quiero trabajar.”',
  by: '— Ejemplo ilustrativo de una terapeuta ocupacional.'
}, {
  quote: '“Contar con fichas imprimibles y recursos de planificación hace que sea más sencillo organizar los materiales antes de cada encuentro.”',
  by: '— Ejemplo ilustrativo de un profesional.'
}, {
  quote: '“La guía de adaptación de actividades ofrece ideas útiles para pensar distintas formas de presentar una tarea.”',
  by: '— Ejemplo ilustrativo de una profesional de rehabilitación.'
}, {
  quote: '“Me gusta que el kit incluya herramientas de organización además de las actividades. Complementa los recursos que ya utilizo.”',
  by: '— Ejemplo ilustrativo de un profesional.'
}];
const FAQ: {
  q: string;
  a: string;
  email?: string;
}[] = [{
  q: '¿Para quién está pensado Terapia Lista?',
  a: 'Está dirigido principalmente a terapeutas ocupacionales que trabajan con adultos y personas mayores, y a estudiantes que buscan recursos educativos complementarios.'
}, {
  q: '¿Qué temas incluye el sistema?',
  a: 'Incluye estimulación cognitiva, actividades de la vida diaria, motricidad fina, organización y secuenciación, fichas imprimibles y herramientas para planificar nuevas actividades.'
}, {
  q: '¿Incluye materiales visuales e imprimibles?',
  a: 'La propuesta contempla fichas, organizadores y hojas de actividades en formato digital imprimible. El contenido final debe corresponder con los archivos efectivamente entregados.'
}, {
  q: '¿Incluye recursos listos para utilizar?',
  a: 'Incluye actividades y plantillas concebidas como recursos de apoyo. El profesional debe seleccionar y adaptar cada propuesta según la persona y los objetivos de intervención.'
}, {
  q: '¿Es adecuado para principiantes?',
  a: 'Puede utilizarse como recurso educativo complementario, pero no reemplaza la formación profesional ni la supervisión que pueda necesitar un estudiante.'
}, {
  q: '¿Incluye actividades para adultos mayores?',
  a: 'Sí, la propuesta está orientada especialmente a profesionales que trabajan con adultos y personas mayores. Las actividades deben adaptarse a las capacidades, los intereses y las necesidades individuales.'
}, {
  q: '¿Incluye los cinco bonos?',
  a: 'La oferta contempla cinco bonos complementarios: planificador de sesiones, registro de observación, guía de adaptación de actividades, organizadores de rutinas y checklist de preparación.'
}, {
  q: '¿Es un producto físico?',
  a: 'No. Terapia Lista es un producto digital.'
}, {
  q: '¿Otorga una certificación profesional?',
  a: 'No. Es un recurso educativo independiente que no otorga matrícula, título ni certificación profesional.'
}, {
  q: '¿Reemplaza la evaluación o el criterio profesional?',
  a: 'No. Las actividades deben seleccionarse y adaptarse según la evaluación individual, los objetivos de intervención y las condiciones de seguridad correspondientes.'
}, {
  q: '¿Cómo recibo mi compra?',
  a: 'El acceso se entrega digitalmente mediante el método de entrega configurado en la tienda, después de completar la compra.'
}, {
  q: '¿Cómo puedo contactar con el soporte?',
  a: 'Podés comunicarte con Nova Market por correo electrónico:',
  email: 'novaamarket034@gmail.com'
}];